import personalHero from "../../assets/services/personal-hero.jpg";
import shoppingHero from "../../assets/services/shopping-hero.jpg";
import travelHero from "../../assets/services/travel-hero.jpg";
import emergencyHero from "../../assets/services/emergency-hero.jpg";
import marriageHero from "../../assets/services/marriage-hero.jpg";
import personalFaq from "../../assets/services/personal-faq.jpg";
import shoppingFaq from "../../assets/services/shopping-faq.jpg";
import travelFaq from "../../assets/services/travel-faq.jpg";
import emergencyFaq from "../../assets/services/emergency-faq.jpg";
import marriageFaq from "../../assets/services/marriage-faq.jpg";

import shoppingCartIcon from "../../assets/services/shopping-cart.svg";
import planeIcon from "../../assets/services/plane.svg";
import heartIcon from "../../assets/services/heart.svg";
import alertCircleIcon from "../../assets/services/alert-circle.svg";
import zapIcon from "../../assets/services/zap.svg";
import smartphoneIcon from "../../assets/services/smartphone.svg";
import shieldIcon from "../../assets/services/shield.svg";
import scaleIcon from "../../assets/services/scale.svg";
import fileTextIcon from "../../assets/services/file-text.svg";
import eyeIcon from "../../assets/services/eye.svg";
import savingsIcon from "../../assets/services/savings.svg";
import globeIcon from "../../assets/services/globe.svg";
import creditCardIcon from "../../assets/services/credit-card.svg";
import sofaIcon from "../../assets/services/sofa.svg";
import monitorIcon from "../../assets/services/monitor.svg";
import homeIcon from "../../assets/services/home.svg";
import partyIcon from "../../assets/services/party.svg";
import rupeeIcon from "../../assets/services/rupee.svg";
import docLinesIcon from "../../assets/services/doc-lines.svg";
import plusMinusIcon from "../../assets/services/plus-minus.svg";
import plane24Icon from "../../assets/services/plane-24.svg";
import busIcon from "../../assets/services/bus.svg";
import templeIcon from "../../assets/services/temple.svg";
import beachIcon from "../../assets/services/beach.svg";
import rupeeCircleIcon from "../../assets/services/rupee-circle.svg";
import docTextIcon from "../../assets/services/doc-text.svg";
import shieldAlertIcon from "../../assets/services/shield-alert.svg";
import plus24Icon from "../../assets/services/plus-24.svg";
import stethoscopeIcon from "../../assets/services/stethoscope.svg";
import receiptIcon from "../../assets/services/receipt.svg";
import usersIcon from "../../assets/services/users.svg";
import handCoinsIcon from "../../assets/services/hand-coins.svg";
import infoIcon from "../../assets/services/info.svg";
import venueIcon from "../../assets/services/venue.svg";
import shirtIcon from "../../assets/services/shirt.svg";
import buildingIcon from "../../assets/services/building.svg";

export interface IconCard {
  icon: string;
  title: string;
  text: string;
  chip?: string;
}

export interface LabelValue {
  label: string;
  value: string;
}

export interface SectionHeading {
  eyebrow: string;
  title: string;
  text?: string;
}

export interface ServicePageData {
  slug: string;
  navLabel: string;
  metaTitle: string;
  metaDescription: string;
  utmSource: string;
  hero: {
    eyebrow?: string;
    title: string;
    text: string;
    chips: string[];
    secondaryCta: string;
    secondaryCtaScrollsToAbout?: boolean;
    note: string;
    image: string;
    imagePosition: string;
  };
  about: SectionHeading & {
    paragraphs: string[];
    cards?: IconCard[];
    cta?: string;
  };
  eligibility: SectionHeading & {
    salaried: LabelValue[];
    selfEmployedNote: string;
    selfEmployed: LabelValue[];
    note: string;
  };
  documents: SectionHeading & {
    salaried: LabelValue[];
    selfEmployed: LabelValue[];
  };
  loanTypes: SectionHeading & { cards: IconCard[] };
  steps: SectionHeading & { items: { title: string; text: string }[] };
  stepsBeforeLoanTypes?: boolean;
  trust: SectionHeading & { cards: IconCard[] };
  calculator: SectionHeading & { variant: "rate" | "fixed" };
  faq: SectionHeading & {
    image: string;
    imagePosition: string;
    items: { question: string; answer: string }[];
  };
  banner?: {
    variant: "split" | "center";
    title: string;
    text: string;
    primaryCta: string;
    secondaryCta?: string;
    badges?: string[];
  };
}

const brandName = import.meta.env.VITE_COMPANY_NAME;
const nbfcName = import.meta.env.VITE_COMPANY_NBFC;
const nbfcLicense = import.meta.env.VITE_COMPANY_NBFC_LICENSE;

const KFS_NOTE =
  "Before you accept, you get a short document with the key terms of your loan: the amount, interest, all fees with GST, the APR, your repayment date and the total you repay. RBI requires this for every digital loan.";

export const servicePages: ServicePageData[] = [
  {
    slug: "personal-loan",
    navLabel: "Personal Loan",
    metaTitle: "Get Instant Online Personal Loan | ToofanLoan",
    metaDescription: "Apply for instant personal loans from ₹10K to ₹3 Lakhs. 100% digital KYC, transparent terms & fast bank transfer via RBI-registered lending partners.",
    utmSource: "Personal_Loan",
    hero: {
      eyebrow: "Funds To You",
      title: "Get the Financial Support You Need in Just 10 Minutes",
      text: `${brandName} offers an online loan for urgent money needs. Apply on your phone with no branch visit. Your offer shows every charge and your repayment date before you accept.`,
      chips: ["All charges told upfront", "Easy online repayment", "RBI-regulated lending partner"],
      secondaryCta: "Check Eligibility",
      note: "Data used with your consent. Terms shared in simple language. Loan details visible in your account.",
      image: personalHero,
      imagePosition: "50% 50%",
    },
    about: {
      eyebrow: "Get Easy Personal Loan Now",
      title: "What is a Personal Loan?",
      paragraphs: [
        `A personal loan is an unsecured loan that helps you manage planned expenses or unexpected financial needs without pledging any collateral. With ${brandName}, eligible salaried professionals can apply for a personal loan through a simple digital process and get access to funds, subject to eligibility and approval.`,
        "Whether it's for medical expenses, travel, home needs, or other personal requirements, a personal loan can help you manage expenses with a clear repayment plan.",
      ],
      cta: "Apply for a Personal Loan",
    },
    eligibility: {
      eyebrow: "Check Before You Apply",
      title: "Personal Loan Eligibility Criteria",
      salaried: [
        { label: "Age", value: "21 to 60 years" },
        { label: "Income", value: "Minimum monthly salary of ₹ 35,000" },
        { label: "Employment", value: "Working with a private or public employer for at least 6 months" },
        { label: "Verification", value: "Salary account or bank statements for the last 3-6 months" },
      ],
      selfEmployedNote: KFS_NOTE,
      selfEmployed: [
        { label: "Age", value: "24 to 60 years" },
        { label: "Business age", value: "Running your business for at least 1-2 years" },
        { label: "Income", value: "Minimum yearly income as per the lender's eligibility policy" },
        { label: "Business type", value: "Business types accepted as per the lender's policy" },
      ],
      note: "A CIBIL/credit-bureau check is part of every application. If your score is limited or low, we still consider you using alternate income and repayment signals.",
    },
    documents: {
      eyebrow: "Keep Documents Ready",
      title: "Documents Needed for Your Personal Loan Application",
      text: "Keep these ready before you apply for instant personal loan. Your KYC is done online, so you don't need to visit a branch or submit paper copies.",
      salaried: [
        { label: "PAN card:", value: "For identity verification purposes." },
        { label: "Aadhaar:", value: "For digital KYC. The mobile number should be linked to it." },
        { label: "Selfie:", value: "Video verification through the mobile phone." },
        { label: "Salary slips:", value: "For the last 3-6 months." },
        { label: "Bank statement:", value: "Of the salary account of last 3-6 months." },
        { label: "Employment proof:", value: "(Company ID card or offer letter)." },
      ],
      selfEmployed: [
        { label: "PAN card:", value: "For identity verification purposes." },
        { label: "Aadhaar:", value: "For digital KYC. The mobile number should be linked to it." },
        { label: "Selfie:", value: "Video verification through the mobile phone." },
        { label: "Business proof:", value: "(GST registration, Udyam or shop license)" },
        { label: "Income Tax returns:", value: "Last 1-2 financial years." },
        { label: "Bank statement:", value: "Last 3-6 months." },
      ],
    },
    loanTypes: {
      eyebrow: "Get Easy Personal Loan Now",
      title: "Find the Right Personal Loan for You",
      text: "Explore loan options designed around different financial needs and situations",
      cards: [
        { icon: shoppingCartIcon, title: "Shopping Loan", text: "Buy something now on account, like household goods or electronics and settle the payment within the allotted period of time.", chip: "Big purchases" },
        { icon: planeIcon, title: "Travel Loan", text: "Pay for your tickets or other expenses related to traveling and pay it back within the set deadline.", chip: "Trips and tickets" },
        { icon: heartIcon, title: "Marriage Loan", text: "Manage wedding costs like venue, outfits and gifts without using up your savings", chip: "Family occasions" },
        { icon: alertCircleIcon, title: "Emergency Loan", text: "Use money available for urgent expenses, like medical emergencies or urgent repairs.", chip: "Urgent requirements" },
      ],
    },
    steps: {
      eyebrow: "Five Steps",
      title: "How to Apply for a Personal Loan",
      text: "A simple process designed to keep your application fast and transparent.",
      items: [
        { title: "Sign up", text: "Enter your mobile number and PAN to start your application." },
        { title: "Video KYC", text: "Take a short, secure selfie check on your phone to verify your identity." },
        { title: "Select your amount", text: "Choose the amount you need from your approved limit." },
        { title: "Review your offer", text: "Check the loan amount, charges and repayment date, then accept if they suit you." },
        { title: "Receive your money", text: "After you accept, the amount is sent to your own bank account." },
      ],
    },
    trust: {
      eyebrow: "Trust and safety",
      title: `Why Choose ${brandName}`,
      text: "Quick Disbursal",
      cards: [
        { icon: zapIcon, title: "Quick Disbursal", text: "After approval, the amount is disbursed to your verified account in around 10 minutes." },
        { icon: smartphoneIcon, title: "Digital KYC", text: "Prove your identity through video KYC on mobile and avoid visiting the branch office." },
        { icon: shieldIcon, title: "No Collateral Required", text: "This is an unsecured loan for salaried and self-employed individuals." },
        { icon: scaleIcon, title: "Responsible Lending", text: "Your financial capacity will be assessed before making an offer based on your capability." },
        { icon: fileTextIcon, title: "Transparent", text: "Details of all the fees and GST are clearly mentioned in the Key Fact Statement." },
        { icon: eyeIcon, title: "Review Your KFS", text: "Check the applicable APR, fees, charges, repayment date and schedule before accepting the loan." },
      ],
    },
    calculator: {
      eyebrow: "Calculate Now",
      title: "Personal Loan APR Calculator",
      text: "For the full yearly picture, use our APR Calculator for Personal Loan before you decide.",
      variant: "rate",
    },
    faq: {
      eyebrow: "Get Your Queries Solved",
      title: "Frequently Asked Questions",
      image: personalFaq,
      imagePosition: "62% 50%",
      items: [
        { question: `How do I apply for a personal loan on ${brandName}?`, answer: "To apply for a personal loan, visit our website and sign up with your mobile number and PAN, complete a video KYC, check your offer, and accept it. The money goes to your bank account." },
        { question: "How fast can I get an Instant Loan Online?", answer: "It can be as quick as 10 minutes once your KYC and details are verified." },
        { question: "What is the repayment period?", answer: "It is 90 to 365 days. Your exact date is shown before you accept." },
        { question: "How is my personal loan interest rate decided?", answer: `We at ${brandName} consider your income, repayment ability and credit history. The rate and APR are shown on your offer before you accept.` },
        { question: "What charges will I pay when I take the loan?", answer: "You need to pay charges for the loan such as Interest, and a processing fee of (up to 10%) plus 18% GST. All charges are shown upfront." },
        { question: "Can I close the loan early? What are the personal loan foreclosure terms?", answer: "Yes, you can close the loan early. Any applicable foreclosure charges are shown in your Key Fact Statement (KFS) before you accept." },
        { question: "How do I repay?", answer: `Repayment is easy and done online. Pay only through the official ${brandName} website or links, never to a personal account.` },
        { question: "Will my application always be approved?", answer: "Yes, mostly as approval depends on your profile and the lender's checks. Checking your eligibility is quick and free." },
        { question: `Is ${brandName} RBI-regulated?`, answer: `Yes, ${brandName} is a Lending Service Provider. The loan is given by ${nbfcName}, an RBI-registered NBFC (licence ${nbfcLicense}).` },
      ],
    },
    banner: {
      variant: "split",
      title: "Your personal loan could be just minutes away.",
      text: "Check your eligibility quickly, with no impact on your credit score.",
      primaryCta: "Apply Personal Loan Online",
      secondaryCta: "Check Eligibility",
      badges: ["No hidden costs", "RBI regulated"],
    },
  },
  {
    slug: "personal-loan/shopping-loan",
    navLabel: "Shopping Loan",
    metaTitle: "Apply for Instant Online Shopping Loan | ToofanLoan",
    metaDescription: "Get quick digital approval and funds in 10 minutes for your purchases. Transparent interest rates, 100% paperless KYC, and no early closure fees.",
    utmSource: "Shopping_Loan",
    hero: {
      eyebrow: "Shop with Ease",
      title: "Make Your Next Purchase in 10 Minutes with a Shopping Loan",
      text: `${brandName} gives you a loan for shopping, so that when buying a gift, a gadget, or a home upgrade, does not need to wait. Apply online from home.`,
      chips: ["Charges shown before accepting", "Simple online repayment", "RBI-regulated lending partner"],
      secondaryCta: "Know More",
      secondaryCtaScrollsToAbout: true,
      note: "Apply from your own phone. Video KYC replaces paper forms. Choose your amount within your limit. Check eligibility before you apply.",
      image: shoppingHero,
      imagePosition: "23% 50%",
    },
    about: {
      eyebrow: "Get Easy Personal Loan Now",
      title: "What is a Shopping Loan?",
      paragraphs: [
        "A shopping loan provides you with funds to buy something, such as different appliances or furniture. You can buy what you need today instead of waiting months to save for it. Applying is done on your phone, with no queues.",
        `Request an instant shopping loan on ${brandName} right from home. Once approved, money transfers to your bank account in about 10 minutes, and repay online.`,
      ],
      cards: [
        { icon: shoppingCartIcon, title: "Buy Easily", text: "Purchase the product you need immediately and pay back the loan in 90 to 365 days." },
        { icon: savingsIcon, title: "Savings Secure", text: "Your savings will be unaffected, so they can be used in case of emergencies and other plans." },
        { icon: globeIcon, title: "Shop Everywhere", text: "Money is credited to your account, thus allowing you to purchase from any shop or website." },
        { icon: creditCardIcon, title: "Improve Credit Rating", text: "Payment details reach the credit bureau, helping improve your credit rating over time." },
      ],
    },
    eligibility: {
      eyebrow: "Check Before You Apply",
      title: "Shopping Loan Eligibility Criteria",
      salaried: [
        { label: "Age", value: "21 to 58 years" },
        { label: "Income", value: "Monthly salary of at least ₹35,000" },
        { label: "Employment", value: "At least 6 months with your current private or public sector employer." },
        { label: "Verification", value: "Your salary account statements for the most recent 3-6 months." },
      ],
      selfEmployedNote: KFS_NOTE,
      selfEmployed: [
        { label: "Age", value: "24 to 60 years" },
        { label: "Business age", value: "Business must be at least 1-2 years old" },
        { label: "Income", value: "Minimum annual income as per the lender's eligibility policy" },
        { label: "Business type", value: "Business types accepted as per the lender's policy" },
      ],
      note: "A check at the credit bureau/CIBIL is done in all cases. If your score is low or poor, then we evaluate you based on other criteria as well.",
    },
    documents: {
      eyebrow: "Gather Documents",
      title: "Documents Required for Your Shopping Loan Application",
      text: "Papers that are ready beforehand make your application easier to complete, so you can check before applying for an instant shopping loan. KYC is done online without visiting the branch.",
      salaried: [
        { label: "PAN card:", value: "Confirms who you are." },
        { label: "Aadhaar:", value: "For digital KYC. The mobile number should be linked to it." },
        { label: "Selfie:", value: "Video verification through the mobile phone." },
        { label: "Salary slips:", value: "For the last 3-6 months." },
        { label: "Bank statement:", value: "Of the salary account of last 3-6 months." },
        { label: "Employment proof:", value: "(Company ID card or offer letter)." },
      ],
      selfEmployed: [
        { label: "PAN card:", value: "Confirms who you are." },
        { label: "Aadhaar:", value: "For digital KYC. The mobile number should be linked to it." },
        { label: "Selfie:", value: "Video verification through the mobile phone." },
        { label: "Business proof:", value: "(GST registration, Udyam or shop license)" },
        { label: "Income Tax returns:", value: "Last 1-2 financial years." },
        { label: "Bank statement:", value: "Last 3-6 months." },
      ],
    },
    loanTypes: {
      eyebrow: "Turn Your Shopping Plans into Reality",
      title: "Choose the Right Loan for Your Shopping Needs",
      text: "See how a shopping loan India fits your needs",
      cards: [
        { icon: sofaIcon, title: "Furniture Buying", text: "You can renovate your living space or room with a new bed, cozy sofa, or an aesthetic wardrobe that improves your home.", chip: "Festive Needs" },
        { icon: monitorIcon, title: "Electronics Purchase", text: "Get a phone, laptop, or television with an online shopping loan, and that too without emptying all your savings.", chip: "Tech Buys" },
        { icon: homeIcon, title: "Home Appliances", text: "Replace a faulty refrigerator, washing machine, or air conditioner to keep the everyday household running smoothly without delays.", chip: "For Home" },
        { icon: partyIcon, title: "Festive Shopping", text: "Prepare for festivals by using a loan for shopping and purchase gifts, traditional clothing, decorations and more within your available budget.", chip: "Festive Needs" },
      ],
    },
    steps: {
      eyebrow: "Apply Quick",
      title: "How To Apply for a Shopping Loan",
      text: "A simple process designed to keep your application fast and transparent.",
      items: [
        { title: "Sign up", text: "Start at home by simply entering your mobile number and PAN number." },
        { title: "Video KYC", text: "Upload a quick video of yourself on your mobile to help set up your identity." },
        { title: "Select your amount", text: "Choose the amount that covers the cost of what you are buying within your limit." },
        { title: "Review your offer", text: "Review the offer before applying; read the terms and conditions carefully." },
        { title: "Receive your money", text: "The instant shopping loan amount is transferred to your account and you are ready to pay." },
      ],
    },
    stepsBeforeLoanTypes: true,
    trust: {
      eyebrow: "Secure and Safe",
      title: `Why Choose ${brandName} for Shopping`,
      text: "Quick Disbursal",
      cards: [
        { icon: smartphoneIcon, title: "Digital Identity Verification", text: "Carry out your KYC procedure digitally through your device to verify your identity and use a shopping loan India to purchase." },
        { icon: fileTextIcon, title: "Clear Loan Information", text: "Read your Key Fact Statement beforehand (KFS) to know the interest rates, APR, fees, and other repayment details." },
        { icon: scaleIcon, title: "Responsible Services", text: "Data is taken with consent and your application is checked with responsibility. Our online method is safe and secure." },
        { icon: zapIcon, title: "Approval in Minutes", text: "Get a loan approval decision within 10 minutes and take action with your online shopping loan plans." },
        { icon: eyeIcon, title: "No Surprise Charges", text: "Know the applicable charges upfront so you can make informed decisions without unexpected costs." },
        { icon: shieldIcon, title: "Flexible Loan Amount", text: "Explore available loan amounts to help finance your shopping purchases according to your needs and budget." },
      ],
    },
    calculator: {
      eyebrow: "Calculate Now",
      title: "Shopping Loan APR Calculator",
      text: "Type in your amount to see what your shopping loan costs over a full year.",
      variant: "rate",
    },
    faq: {
      eyebrow: "Clear Your Doubts",
      title: "Frequently Asked Questions about Shopping Loan",
      image: shoppingFaq,
      imagePosition: "18% 50%",
      items: [
        { question: "How to apply for shopping loan online?", answer: `Applying for shopping loan is simple using ${brandName}. Fill out your information, do the video KYC, select your desired amount, and go ahead and accept your offer.` },
        { question: "What loan amount will I be able to avail for shopping?", answer: "You can apply for a loan that starts at ₹10,000 to ₹3,00,000 based on your eligibility and the available offer for you." },
        { question: "How fast will I get my shopping loan amount?", answer: "Your shopping loan amount will be credited to your bank account within 10 minutes once your application is processed." },
        { question: "What can I purchase using a shopping loan?", answer: "A shopping loan can be used to purchase products such as smartphones, laptops, TVs, furniture, appliances, gifts, etc." },
        { question: "Which documents are needed for shopping loan?", answer: "The following documents will be needed for a shopping loan: PAN Card, Aadhaar for digital KYC, and a selfie/video verification. Other documents may be needed based on your occupation." },
        { question: "What would be the cost of my shopping loan?", answer: "You can know the interest rate, APR, processing fees, GST, and any other applicable costs from the loan offer and KFS." },
        { question: "Is it possible to repay the shopping loan online?", answer: "Yes, you can pay off the loan online following the prearranged payment period, which may last from 90 days up to 365 days." },
        { question: "Can I qualify for a shopping loan despite having a poor credit score?", answer: "Yes, you can still apply with a poor credit score. We will check other factors such as income, business profits, or other criteria as well before approval." },
      ],
    },
    banner: {
      variant: "center",
      title: "Bring Your Shopping Plans Closer to Reality",
      text: "Explore shopping loan options online and find financing for your next purchase.",
      primaryCta: "Apply for a Shopping Loan Online",
    },
  },
  {
    slug: "personal-loan/travel-loan",
    navLabel: "Travel Loan",
    metaTitle: "Instant Travel Loan Online | RBI Registered - ToofanLoan",
    metaDescription: "Borrow ₹10,000 to ₹3,00,000 for tours and flights. RBI-regulated lending partner, simple video KYC, and transparent upfront fees. Apply today.",
    utmSource: "Travel_Loan",
    hero: {
      title: "Instant Travel Loan in Minutes: Book Flights and Hotels Today",
      text: `${brandName} offers a loan for travel that covers flights, stays and local rides. Choose your dates first and book when the fare suits you. Apply online from home today.`,
      chips: ["Pay back online", "RBI-registered", "Book flights, hotels, holiday packages"],
      secondaryCta: "Check Eligibility",
      note: "Book flights, stays, and tours with any airline or agent, transfer money to your own account, and see all costs upfront before accepting.",
      image: travelHero,
      imagePosition: "50% 50%",
    },
    about: {
      eyebrow: "Travel without Worrying",
      title: "What is Travel Loan?",
      paragraphs: [
        "A travel loan provides you with funds to book flights, a hotel, or a tour. Apply for an instant travel loan in minutes from your phone. Fares and rooms often cost more when you book just before the day, but booking early gives you discounts and offers.",
        "People use this loan for urgent travel meetings, buying stuff when visiting places, and booking expensive or good resorts.",
      ],
      cards: [
        { icon: rupeeIcon, title: "Fixed Amount", text: "You receive one sum to pay for flights, stay, and tours, instead of paying in pieces." },
        { icon: docLinesIcon, title: "Accurate Details", text: "Use one name on your loan and tickets, exactly as on your ID." },
        { icon: creditCardIcon, title: "Credit Score", text: "A clean repayment record can make your next trip easier to finance." },
        { icon: plusMinusIcon, title: "Loan Difference", text: "A loan for holiday plans lets you pick any airline or hotel, not just one fixed package deal." },
      ],
    },
    eligibility: {
      eyebrow: "Check Before You Apply",
      title: "Travel Loan Eligibility Criteria",
      salaried: [
        { label: "Age", value: "21 to 58 years" },
        { label: "Income", value: "Monthly salary of ₹35,000 or above" },
        { label: "Employment", value: "Minimum 6 months of service with your current company, private or government" },
        { label: "Verification", value: "Statements of your salary account covering the latest 3-6 months" },
      ],
      selfEmployedNote: KFS_NOTE,
      selfEmployed: [
        { label: "Age", value: "24 to 60 years" },
        { label: "Business age", value: "Your business is 1-2 years old or older" },
        { label: "Income", value: "Minimum yearly earnings as per the lender's eligibility policy" },
        { label: "Business type", value: "Business categories accepted as per the lender's policy" },
      ],
      note: "The CIBIL credit bureau check comes with every application of travel loan in India. In case your score is either poor or has limits, we will consider your application based on alternative information sources.",
    },
    documents: {
      eyebrow: "Keep Documents Ready",
      title: "Documents Needed for Your Travel Loan Application",
      text: "Gather these papers when applying financing for travel, since trips often have fixed dates, and request once they are ready. KYC is done fully online with no branch visits.",
      salaried: [
        { label: "PAN card:", value: "It is used as an identification document." },
        { label: "Selfie:", value: "A video clip recorded on your mobile phone." },
        { label: "Salary slips:", value: "3 to 6 months' Salary slips" },
        { label: "Bank statement:", value: "Salary bank statements for 3 to 6 months" },
        { label: "Employment proof:", value: "Staff ID card/joining letter" },
        { label: "Aadhaar:", value: "Mandatory for digital KYC, which is linked to your mobile phone number" },
      ],
      selfEmployed: [
        { label: "PAN card:", value: "Used to prove identity." },
        { label: "Aadhaar:", value: "Needed for digital KYC; please link it to your mobile number." },
        { label: "Selfie:", value: "A short video taken on a mobile." },
        { label: "Business proof:", value: "(GST certificate, Udyam or trade licence)" },
        { label: "Income Tax returns:", value: "Latest 1-2 years filed" },
        { label: "Bank statement:", value: "Last 3-6 months." },
      ],
    },
    loanTypes: {
      eyebrow: "Stop delaying your plans and start travelling now",
      title: "Find the Right Travel Loan for You",
      text: "See which type of trips this Travel Loan in India covers:",
      cards: [
        { icon: plane24Icon, title: "Tickets", text: "With a loan for travel, book flights, trains and more while fares are low, whenever it is a business trip, a visit home or a short weekend away.", chip: "Air Travel" },
        { icon: busIcon, title: "Family or Meeting Trips", text: "Take your loved ones together without delaying it until next year even when going for an urgent outstation meeting.", chip: "Time Together" },
        { icon: templeIcon, title: "Pilgrimage Journeys", text: "Visit a holy place with your family when the season suits you and relax a little without using all savings.", chip: "Spiritual Trips" },
        { icon: beachIcon, title: "Holiday Packages", text: "Plan a full break with hotels, meals and sightseeing in one go; vacation loans suit carefully planned getaways.", chip: "Plan Your Holiday" },
      ],
    },
    steps: {
      eyebrow: "Application Procedure",
      title: "How to Apply for a Travel Loan",
      items: [
        { title: "Sign up", text: "Register using a phone number and PAN while you plan." },
        { title: "Video KYC", text: "Record a short selfie video so we can confirm the applicant is you." },
        { title: "Select your amount", text: "Add up flights, stay and local travel, then enter that total." },
        { title: "Review your offer", text: "Check charges and due date on the offer before you pay airlines or hotels." },
        { title: "Receive your money", text: "Money arrives in your account, so you book flights, stays and tours." },
      ],
    },
    trust: {
      eyebrow: "Responsible Lending",
      title: `Why Choose ${brandName} for Travel`,
      cards: [
        { icon: smartphoneIcon, title: "Quick Disbursement", text: "Your loan amount will be credited to your bank account in approximately 10 minutes after being approved." },
        { icon: fileTextIcon, title: "Digital KYC", text: "This is the best loan for travelling because an easy identity verification process by filming yourself via the video call feature of your mobile phone." },
        { icon: scaleIcon, title: "No Collateral Needed", text: `Your personal possessions, tickets, and other securities are not needed to apply for a loan with ${brandName}.` },
        { icon: zapIcon, title: "Responsible Borrowing", text: "It helps assess if you have the ability to pay back the loan amount based on your financial status prior to approval." },
        { icon: eyeIcon, title: "Transparent Terms", text: "When applying for an instant travel loan in minutes the fees and charges levied along with GST will be detailed in the Key Fact Statement." },
        { icon: shieldIcon, title: "Check Your KFS", text: "Review your Key Fact Statement to understand the APR applicable to the loan before accepting it." },
      ],
    },
    calculator: {
      eyebrow: "Calculate Now",
      title: "Travel Loan APR Calculator",
      text: "Work out your travel loan costs across a year before you confirm any booking.",
      variant: "fixed",
    },
    faq: {
      eyebrow: "Get Your Queries Solved",
      title: "Frequently Asked Questions",
      image: travelFaq,
      imagePosition: "71% 50%",
      items: [
        { question: "How fast can I get my travel loan funds?", answer: "Once approved, money usually shows in your account within about 10 minutes, so booking begins." },
        { question: "What is the repayment period?", answer: "Repayment runs for 90 to 365 days. The offer itself shows your exact date." },
        { question: "How is my travel loan interest rate decided?", answer: "To set your rate, we review your income and credit record. The best loan for travelling states its rate and APR clearly up front." },
        { question: "What charges will I pay when I take the loan?", answer: "A loan for travel carries interest, a processing fee of up to 10% and 18% GST on that fee. Your offer lists each charge before you book." },
        { question: "Can I close the loan early? What are the travel loan foreclosure terms?", answer: "Yes, you can foreclose whenever you wish. No early closure charge applies." },
        { question: "How do I repay?", answer: "Pay back online from your phone, even while you are away travelling. Use only our official website, never a personal account." },
        { question: "Will my application always be approved?", answer: "Yes, mostly as approval depends on your profile and other criteria. Checking your eligibility is quick and free." },
        { question: `Is ${brandName} RBI-regulated?`, answer: `Yes, ${brandName} is a lending service provider. ${nbfcName}, an RBI-registered NBFC (licence ${nbfcLicense}), issues your loan after approval.` },
      ],
    },
    banner: {
      variant: "center",
      title: "Make your next trip easier to plan with a travel loan.",
      text: "Check your eligibility online in minutes, without affecting your credit score.",
      primaryCta: "Get a Travel Loan Online",
    },
  },
  {
    slug: "personal-loan/emergency-loan",
    navLabel: "Emergency Loan",
    metaTitle: "Emergency Medical Loans Online | ToofanLoan",
    metaDescription: "Handle unexpected health & urgent costs fast. Apply online in minutes via digital KYC. Flexible 90–365 day repayment plans with full APR disclosure.",
    utmSource: "Emergency_Loan",
    hero: {
      title: "Get Help in Managing Urgent Bills With an Emergency Loan",
      text: `${brandName} offers an emergency loan online for urgent bills, repairs and urgent needs. Apply from home in minutes and receive funds to handle any financial situation.`,
      chips: ["RBI-registered", "Repay from mobile", "Medical, repairs, urgent bills"],
      secondaryCta: "Check Eligibility",
      note: "Apply from your own phone, pay hospitals, shops, or tradesmen, receive money directly into your own account, and see all costs upfront before proceeding.",
      image: emergencyHero,
      imagePosition: "100% 50%",
    },
    about: {
      eyebrow: "Urgent Needs Solved",
      title: "What is an Emergency Loan?",
      paragraphs: [
        "A medical bill, a leaking pipe in the kitchen, or any sudden repair needs funds to fix all these. With an instant emergency loan, you can pay for these urgent expenses when you do not have enough savings.",
        "After verification, your application will be approved, and the money will reach your bank account. This helps you deal with the expenses without unnecessary delays.",
      ],
      cards: [
        { icon: rupeeCircleIcon, title: "Borrow What You Need", text: "Figure out the cost and then apply for the loan to pay off the expense without causing any extra burden." },
        { icon: docTextIcon, title: "Maintain the Paperwork", text: "Keep your estimates and the necessary papers in order so that you do not take much time filling out the application form." },
        { icon: shieldAlertIcon, title: "Pay for Urgent Needs", text: "With medical emergency loan a sudden need, big or small, can be helped with an emergency loan online very easily." },
        { icon: plus24Icon, title: "Calculate Total Cost", text: "Check out all the rates, processing fees, and other costs involved in the loan before making the final decision." },
      ],
    },
    eligibility: {
      eyebrow: "Check Before You Apply",
      title: "Eligibility Criteria for Emergency Loan",
      salaried: [
        { label: "Age", value: "21 to 58 years old" },
        { label: "Income", value: "₹35,000 per month or more" },
        { label: "Employment", value: "6 months or more of experience in your current private/public organization" },
        { label: "Proof", value: "Bank statements proving income for the last 3-6 months" },
      ],
      selfEmployedNote: KFS_NOTE,
      selfEmployed: [
        { label: "Age", value: "24 to 60 years" },
        { label: "Business age", value: "1-2 years old" },
        { label: "Income", value: "Minimum yearly income as per the lender's eligibility policy" },
        { label: "Business type", value: "Business types accepted as per the lender's policy" },
      ],
      note: "A CIBIL/credit bureau report is mandatory for all applications. If your score is restricted or low, we still evaluate your application based on alternative income and payment indicators.",
    },
    documents: {
      eyebrow: "Keep Documents Ready",
      title: "Documents Needed for Your Emergency Loan Application",
      text: "Keep these papers ready to upload so that your instant emergency loan request is approved smoothly. KYC is completed online, with no branch visit.",
      salaried: [
        { label: "PAN card:", value: "Required for proof of identity" },
        { label: "Selfie:", value: "A short video verification through mobile phone" },
        { label: "Salary slips:", value: "Salary slips of the last 3-6 months" },
        { label: "Bank statement:", value: "Of your salary account for the last 3-6 months" },
        { label: "Employment proof:", value: "(ID card/service letter of your employer)" },
        { label: "Aadhaar:", value: "Required for digital KYC; mobile number needs to be linked to it" },
      ],
      selfEmployed: [
        { label: "PAN card:", value: "Required for proof of identity" },
        { label: "Aadhaar:", value: "Required for digital KYC; mobile number needs to be linked to it" },
        { label: "Selfie:", value: "Short video verification through mobile phone" },
        { label: "Business proof:", value: "(GST document/ Udyam/ Shop license)" },
        { label: "Income Tax returns:", value: "ITR for the last 1-2 years" },
      ],
    },
    loanTypes: {
      eyebrow: "Get Easy Emergency Loan Now",
      title: "Find the Right Emergency Loan for You",
      text: "Emergency Personal Loan: See which urgent needs this loan covers.",
      cards: [
        { icon: stethoscopeIcon, title: "Medical Expenses", text: "An emergency loan online for medical expenses helps you pay for hospitalization, testing, medicines, or treatment.", chip: "Health Expense" },
        { icon: receiptIcon, title: "Unforeseen Repair Bills", text: "Deal with bursting pipes, damage to your car, or a broken appliance to avoid any further complications.", chip: "House & Vehicle" },
        { icon: usersIcon, title: "Family Expenses", text: "Handle any urgent expenses of family with loan emergency amount and without compromising your other monthly expenditures.", chip: "Unexpected Events" },
        { icon: handCoinsIcon, title: "Fees Payments", text: "Make payments for school, college, or exam fees in case you have any unforeseen expenses that make you lack funds for payment.", chip: "Time-Bound Costs" },
      ],
    },
    steps: {
      eyebrow: "Easy Application",
      title: "How to Apply for an Emergency Loan",
      items: [
        { title: "Sign up", text: "Start with your phone number and PAN; nothing else is needed." },
        { title: "Video KYC", text: "Take a quick video selfie so we can confirm exactly who is applying." },
        { title: "Select your amount", text: "Enter the exact amount your emergency needs, within your approved limit." },
        { title: "Review your offer", text: "Read the full offer carefully: costs, due date and the total payable, then decide." },
        { title: "Receive your money", text: "Funds go straight to your account, ready to settle the urgent bill." },
      ],
    },
    trust: {
      eyebrow: "Support When You Need It",
      title: `Why Choose ${brandName}`,
      cards: [
        { icon: smartphoneIcon, title: "Fast Disbursal", text: "After approval, your loan emergency funds are credited to your bank account in about 10 minutes." },
        { icon: fileTextIcon, title: "KYC Online", text: "Complete your KYC procedure by video call on your phone without making any trip to our office." },
        { icon: scaleIcon, title: "No Need for Collateral", text: "You can receive financial support without using your assets such as gold or property as collateral." },
        { icon: zapIcon, title: "Responsible Borrowing", text: "We check your profile in case of instant emergency loan borrowing to assist you and also to pay back easily." },
        { icon: eyeIcon, title: "Transparent Terms", text: "Know about the fees that are applicable and also GST before you proceed further to accept the loan." },
        { icon: shieldIcon, title: "Check Your KFS", text: "View the APR, fees applicable, and your repayment schedule from the Key Fact Statement before taking the loan." },
      ],
    },
    calculator: {
      eyebrow: "Calculate Now",
      title: "Emergency Loan APR Calculator",
      text: "Work out your emergency loan costs across a year before you accept any offer.",
      variant: "fixed",
    },
    faq: {
      eyebrow: "Get Your Queries Solved",
      title: "Frequently Asked Questions",
      image: emergencyFaq,
      imagePosition: "50% 50%",
      items: [
        { question: `How do I apply for an emergency loan on ${brandName}?`, answer: "To apply for an emergency personal loan, sign up with mobile and PAN details, complete video KYC and enter the amount you need. Review the offer, and the money reaches your bank account once approved." },
        { question: "How fast can I get my emergency loan funds?", answer: "Funds usually show in your account about 10 minutes after approval, so urgent payments start." },
        { question: "What is the repayment period?", answer: "You have 90 to 365 days to repay. The offer states your exact date." },
        { question: "How is my emergency loan interest rate decided?", answer: "Your income, repayment record and credit history guide the rate. You see both the rate and the APR on your offer before going further." },
        { question: "What charges will I pay when I take the loan?", answer: "An emergency loan costs you interest plus a processing fee of up to 10%, with 18% GST on that fee. Each is shown on your offer first." },
        { question: "Can I close the loan early? What are the emergency loan foreclosure terms?", answer: "Yes, early closure is possible. No foreclosure charge applies on this loan." },
        { question: "How do I repay?", answer: "Pay back online by phone in a minute or two. Use only our official website or links, never a personal account." },
        { question: "Will my application always be approved?", answer: "Yes, mostly as approval depends on your profile and the lender's checks. Checking your eligibility is quick and free." },
        { question: `Is ${brandName} RBI-regulated?`, answer: `Yes, ${brandName} is a lending service provider. ${nbfcName}, an RBI-registered NBFC (licence ${nbfcLicense}), issues your loan after approval.` },
      ],
    },
  },
  {
    slug: "personal-loan/marriage-loan",
    navLabel: "Marriage Loan",
    metaTitle: "Marriage Loan Eligibility & Rates | Instant Disbursement Online",
    metaDescription: "Apply for quick wedding financing with no collateral required. Fast 10-minute video KYC disbursement directly to your bank account. Learn more!",
    utmSource: "Marriage_Loan",
    hero: {
      title: "Wedding Loan to Help Bring Your Marriage Plans Together",
      text: `${brandName} offers a marriage loan for venue, catering, outfits, decor and other event costs. Pay vendors on time and keep your savings untouched. Apply online from your phone today.`,
      chips: ["Repay easily through mobile", "Following RBI-registered guidelines", "Apply and pay for destination weddings"],
      secondaryCta: "Check Eligibility",
      note: "Pay your hall, caterer, and decorator, apply between fittings and calls, transfer money directly to vendors from your own account, and know all costs upfront before booking.",
      image: marriageHero,
      imagePosition: "50% 50%",
    },
    about: {
      eyebrow: "Plan Your Wedding",
      title: "What is a Wedding Loan?",
      paragraphs: [
        `Your wedding does not need to be delayed; instead of using all your savings in one go, pay in advance, long before the big day arrives. Book the photographers, venue and caterers in advance with a personal loan for wedding by ${brandName}.`,
        "With this loan, you can manage expenses without worrying about your bank balance. Once approved, your funds will reach your bank account in a short time, such as 10 minutes.",
      ],
      cards: [
        { icon: rupeeCircleIcon, title: "Fixed Amount", text: "Take a single loan amount for advance and vendor payments rather than many smaller amounts" },
        { icon: infoIcon, title: "Correct Information", text: "With the help of a vendor quote, take care to provide the correct amount of money required." },
        { icon: creditCardIcon, title: "Credit Score", text: "Taking out a loan helps build your CIBIL score; pay on time to maintain a healthy score." },
        { icon: plusMinusIcon, title: "Loan Difference", text: "A wedding loan is for one planned event, unlike a credit card that tempts everyday extra spending." },
      ],
    },
    eligibility: {
      eyebrow: "See Who Can Apply",
      title: "Marriage Loan Eligibility Criteria",
      salaried: [
        { label: "Age", value: "21 to 58 years" },
        { label: "Income", value: "Earn ₹35,000 or more each month" },
        { label: "Employment", value: "Six months or more of service at a company or government office" },
        { label: "Verification", value: "Bank records of salary credits for the latest 3-6 months" },
      ],
      selfEmployedNote: KFS_NOTE,
      selfEmployed: [
        { label: "Age", value: "24 to 60 years" },
        { label: "Business age", value: "Business has operated for 1-2 years or more" },
        { label: "Income", value: "Minimum annual earnings as per the lender's eligibility policy" },
        { label: "Business type", value: "Business types accepted as per the lender's policy" },
      ],
      note: "A CIBIL/credit-bureau check is part of every application. If your score is limited or low, we still consider you using alternate income and repayment signals.",
    },
    documents: {
      eyebrow: "Papers You Need",
      title: "Documents Needed for Your Marriage Loan Application",
      text: "Keep these papers ready to confirm your personal loan for wedding without delay. KYC is completed online, so nobody needs to take time off for a branch visit.",
      salaried: [
        { label: "PAN card:", value: "Required to confirm identity." },
        { label: "Selfie:", value: "A brief video check on mobile" },
        { label: "Salary slips:", value: "Salary slips of the last 3-6 months" },
        { label: "Bank statement:", value: "Recent 3-6 months of statements" },
        { label: "Employment proof:", value: "(Work ID card or appointment letter)" },
        { label: "Aadhaar:", value: "Used in digital KYC; make sure your mobile number is linked." },
      ],
      selfEmployed: [
        { label: "PAN card:", value: "Required to confirm identity." },
        { label: "Aadhaar:", value: "Used in digital KYC; make sure your mobile number is linked." },
        { label: "Selfie:", value: "A brief video check on mobile." },
        { label: "Business proof:", value: "(Udyam, GST proof or trade licence)" },
        { label: "Income Tax returns:", value: "Past 1-2 years’ ITR" },
        { label: "Bank statement:", value: "3-6 recent months" },
      ],
    },
    loanTypes: {
      eyebrow: "Wedding Costs This Loan Covers",
      title: "Find the Right Marriage Loan for You",
      cards: [
        { icon: venueIcon, title: "Venue Booking", text: "Make the deposit for the hall, lawn, or banquet earlier to make sure that your chosen date is reserved for you.", chip: "Hall Deposit" },
        { icon: receiptIcon, title: "Vendor Bills", text: "Pay the bills for the caterer, decorator, and photographer with a wedding loan without using your family’s money.", chip: "Pay On Time" },
        { icon: shirtIcon, title: "Wedding Attire", text: "Make arrangements for purchasing the bridal outfits, sherwanis, and other family clothing before all the tailors and boutiques get booked.", chip: "Bridal Outfits" },
        { icon: buildingIcon, title: "Hotel for Relatives", text: "Book a place for your relatives to stay before arriving for the ceremony, before the prices are hiked up.", chip: "Family Arrivals" },
      ],
    },
    steps: {
      eyebrow: "Getting Started",
      title: "How to Apply for a Marriage Loan",
      items: [
        { title: "Sign up", text: "Share your phone number and PAN between vendor calls today." },
        { title: "Video KYC", text: "Capture a short selfie video at home and skip the branch visit entirely." },
        { title: "Select your amount", text: "Enter the wedding expense you need to cover, within your limit." },
        { title: "Review your offer", text: "Read the offer: costs, due date and total payable before paying the vendor or hall." },
        { title: "Receive your money", text: "Funds reach your account, ready for the hall, catering and all vendor payments." },
      ],
    },
    trust: {
      eyebrow: "Why Families Choose",
      title: `Why Choose ${brandName} for Wedding Loan`,
      cards: [
        { icon: smartphoneIcon, title: "Fast Disbursal", text: "Upon approval, money comes in usually within 10 minutes, so that you can make use of it for payment to vendors or the wedding hall." },
        { icon: fileTextIcon, title: "Video based KYC", text: "The KYC procedure is done through phone regardless of where you are; just send a video without visiting any branch." },
        { icon: scaleIcon, title: "Without Collateral", text: "Collateral is not required which ensures that your family wealth remains intact as you make your wedding loan payment arrangements." },
        { icon: zapIcon, title: "Responsible Loan Lender", text: "We assess wedding loan eligibility on your profile first, so the loan suits your situation and your family’s wedding plans." },
        { icon: eyeIcon, title: "See Your KFS", text: "View the applicable annual percentage rate, fees, charges, and repayment schedule of the loan before accepting." },
        { icon: shieldIcon, title: "Transparent Terms", text: "The Key Fact Statement lists the marriage loan interest rate, fees and GST, so your wedding budget includes true costs." },
      ],
    },
    calculator: {
      eyebrow: "Yearly Cost",
      title: "Marriage Loan APR Calculator",
      text: "Check the marriage loan interest rate and yearly cost before you pay any vendor advance.",
      variant: "fixed",
    },
    faq: {
      eyebrow: "Get Your Queries Solved",
      title: "Frequently Asked Questions",
      image: marriageFaq,
      imagePosition: "71% 50%",
      items: [
        { question: `How do I apply for a marriage loan on ${brandName}?`, answer: "To get a personal loan for wedding costs, register with your mobile and PAN, finish video KYC and state the amount you need. Look over the offer, and funds arrive in your account on approval." },
        { question: "How fast can I get my marriage loan funds?", answer: "Funds typically reach your account roughly 10 minutes after approval, so vendors can be paid." },
        { question: "What is the repayment period?", answer: "Repayment is completed within 90 to 365 days. Your offer lists the exact date." },
        { question: "How is my marriage loan interest rate decided?", answer: "The marriage loan interest rate depends on your income, past repayments and credit history. Your offer shows both the rate and the APR first." },
        { question: "What charges will I pay when I take the loan?", answer: "A wedding loan has three costs: a processing fee of up to 10%, GST at 18% on it, and interest. Each one is shown on your offer." },
        { question: "Can I close the loan early? What are the marriage loan foreclosure terms?", answer: "Yes, you may close it ahead of time. No foreclosure charge applies." },
        { question: "How do I repay?", answer: "Pay back online in a few taps, even between wedding errands. Pay only on our official website, never to private individuals." },
        { question: "Will my application always be approved?", answer: "Yes, mostly as approval depends on your profile and income. Even if you have a low CIBIL score we’ll still consider other criteria as well." },
        { question: `Is ${brandName} RBI-regulated?`, answer: `Yes, ${brandName} is a lending service provider. ${nbfcName}, an RBI-registered NBFC (licence ${nbfcLicense}), issues your loan after approval.` },
      ],
    },
    banner: {
      variant: "center",
      title: "Your wedding plans could move forward in minutes.",
      text: "Check your eligibility quickly, with no impact on your credit score.",
      primaryCta: "Apply for Marriage Loan",
    },
  },
];
